import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import Seo, { productJsonLd } from "@/shop/Seo";
import { apiGet, fmt, track } from "@/shop/api";
import { useCart } from "@/shop/CartContext";

export default function ProductPage() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [openSections, setOpenSections] = useState({ delivery: false });

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null);
    setError(false);
    setImgIdx(0);
    setQty(1);
    apiGet(`/api/shop/products/${slug}`)
      .then((prod) => {
        setP(prod);
        track("product_view", { sku: prod.sku, name: prod.name, type: prod.product_type });
      })
      .catch(() => setError(true));
  }, [slug]);

  if (error)
    return (
      <div className="bg-ink text-bone min-h-screen flex flex-col items-center justify-center gap-4">
        <Seo title="Oggetto non trovato — Stillwater" />
        <p className="font-serif italic text-2xl">Questo oggetto non è più in vetrina.</p>
        <Link to="/shop" className="font-mono text-[11px] uppercase tracking-[0.25em] text-ember hover:underline">
          Torna allo shop
        </Link>
      </div>
    );
  if (!p) return <div className="bg-ink min-h-screen" />;

  const images = p.images?.length ? p.images : [{ url: "/photos/tre-canne.jpg", alt: p.name }];
  const maxQty = p.track_inventory ? Math.max(1, Math.min(p.inventory_qty, 10)) : 10;
  const canBuy = !p.sold && p.in_stock;

  const add = () => {
    addItem(p, qty);
    track("add_to_cart", { sku: p.sku, name: p.name, qty, price: p.price });
    toast.success(`${p.name} — aggiunto al carrello`);
  };

  return (
    <div className="bg-ink text-bone min-h-screen" data-testid="product-page">
      <Seo
        title={p.seo_title || `${p.name} — Stillwater Shop`}
        description={p.seo_description || p.short_description || p.description?.slice(0, 155)}
        canonical={`https://stillwatermag.eu/shop/selected-tackle${p.brand_slug ? `/${p.brand_slug}` : ""}/${p.slug}`}
        jsonLd={productJsonLd(p, `/shop/selected-tackle/${p.brand_slug || "-"}/${p.slug}`)}
        ogType="product"
      />

      <div className="px-5 md:px-10 pt-24 md:pt-28 pb-4">
        <Link
          to="/shop/selected-tackle"
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint hover:text-ember transition-colors"
        >
          ← Selected Tackle
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16 px-5 md:px-10 pb-16">
        <div data-testid="product-gallery">
          <motion.div
            key={imgIdx}
            initial={{ opacity: 0.4 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className={`aspect-[4/5] md:aspect-[4/3] overflow-hidden border border-ink-border ${p.product_type === "selected_tackle" ? "bg-bone" : ""}`}
          >
            <img
              src={images[imgIdx].url}
              alt={images[imgIdx].alt || `${p.name} — immagine ${imgIdx + 1}`}
              loading={imgIdx === 0 ? "eager" : "lazy"}
              className={`h-full w-full ${p.product_type === "selected_tackle" ? "object-contain mix-blend-multiply p-3" : "object-cover"}`}
              data-testid="product-main-image"
            />
          </motion.div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
              {images.map((im, i) => (
                <button
                  key={im.url + i}
                  onClick={() => setImgIdx(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`shrink-0 w-20 md:w-24 aspect-square overflow-hidden border transition-colors ${i === imgIdx ? "border-ember" : "border-ink-border hover:border-faint"} ${p.product_type === "selected_tackle" ? "bg-bone" : ""}`}
                >
                  <img
                    src={im.url}
                    alt=""
                    loading="lazy"
                    className={`h-full w-full ${p.product_type === "selected_tackle" ? "object-contain mix-blend-multiply" : "object-cover"}`}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:pt-4">
          <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-ember">
            {p.brand}
          </p>
          <h1 className="mt-3 font-serif font-light text-3xl md:text-5xl tracking-tight leading-tight" data-testid="product-name">
            {p.name}
          </h1>
          <p className="mt-5 font-mono text-xl md:text-2xl text-bone" data-testid="product-price">
            {fmt(p.price)}
            <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-faint">IVA incl.</span>
          </p>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em]" data-testid="product-availability">
            <span className={p.sold ? "text-ember" : p.in_stock ? "text-mist" : "text-faint"}>
              {p.availability_label}
            </span>
            {p.low_stock && !p.sold && (
              <span className="ml-3 text-bone/70">Ultimi {p.inventory_qty} pezzi</span>
            )}
          </p>

          {canBuy && (
            <div className="mt-8 flex items-stretch gap-3" data-testid="add-to-cart-area">
              <div className="flex items-center border border-ink-border rounded-full">
                <button
                  data-testid="qty-minus"
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="px-4 py-3 text-mist hover:text-bone"
                  aria-label="Riduci quantità"
                >
                  <Minus size={14} strokeWidth={1.5} />
                </button>
                <span className="w-8 text-center font-mono text-sm" data-testid="qty-value">{qty}</span>
                <button
                  data-testid="qty-plus"
                  onClick={() => setQty(Math.min(maxQty, qty + 1))}
                  className="px-4 py-3 text-mist hover:text-bone"
                  aria-label="Aumenta quantità"
                >
                  <Plus size={14} strokeWidth={1.5} />
                </button>
              </div>
              <button
                data-testid="add-to-cart"
                onClick={add}
                className="flex-1 inline-flex items-center justify-center gap-3 bg-ember text-ink font-mono text-[11px] uppercase tracking-[0.25em] px-6 rounded-full hover:bg-ember-bright transition-colors duration-300"
              >
                <ShoppingBag size={14} strokeWidth={1.5} />
                Aggiungi al carrello
              </button>
            </div>
          )}

          {p.short_description && (
            <p className="mt-8 text-sm md:text-base text-mist leading-relaxed">{p.short_description}</p>
          )}

          {(p.why_we_stock_it || p.why_we_stock_it_it) && (
            <div className="mt-10 border-t border-ink-border pt-6" data-testid="why-we-stock-it">
              <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">
                Why we stock it
              </h2>
              <p className="mt-4 text-sm md:text-base text-bone/90 leading-relaxed">
                {p.why_we_stock_it_it || p.why_we_stock_it}
              </p>
            </div>
          )}

          {p.description && (
            <div className="mt-10">
              <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">Dettagli</h2>
              <p className="mt-4 text-sm text-mist leading-relaxed whitespace-pre-line">{p.description}</p>
            </div>
          )}

          {p.specifications?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">Specifiche</h2>
              <dl className="mt-4 border-t border-ink-border" data-testid="spec-table">
                {p.specifications.map((s, i) => (
                  <div key={i} className="flex justify-between gap-6 py-3 border-b border-ink-border">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">{s.label}</dt>
                    <dd className="font-serif text-sm text-bone text-right">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="mt-10 border-t border-ink-border">
            <button
              data-testid="delivery-toggle"
              onClick={() => setOpenSections((s) => ({ ...s, delivery: !s.delivery }))}
              className="w-full flex items-center justify-between py-4 font-mono text-[10px] uppercase tracking-[0.3em] text-mist hover:text-bone transition-colors"
            >
              Spedizione e resi
              <ChevronDown size={14} strokeWidth={1.5} className={`transition-transform ${openSections.delivery ? "rotate-180" : ""}`} />
            </button>
            {openSections.delivery && (
              <p className="pb-6 text-sm text-mist leading-relaxed" data-testid="delivery-content">
                Le spese di spedizione vengono calcolate al checkout in base al
                paese di destinazione. Per gli acquisti online vale il diritto
                di recesso di 14 giorni previsto dalla normativa UE.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
